import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Descriptions, Drawer, Empty, Grid, Progress, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  productService,
  type ProductImportHistoryRow,
  type ProductImportHistorySummary,
} from '@/services/product.service';
import { formatDateTime } from '@/utils/format';
import { productKeys } from '@/services/queryKeys';
import { fetchRemainingPages } from '../parts/import-history-pages';

const PAGE_SIZE = 20;
const sourceLabels = {
  salework: 'Salework',
  amis: 'AMIS',
  salework_amis: 'Salework + AMIS',
} as const;

function formatDuration(milliseconds: number): string {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes === 0) return `${remainingSeconds} giây`;
  if (minutes < 60) return `${minutes} phút ${remainingSeconds} giây`;
  const hours = Math.floor(minutes / 60);
  return `${hours} giờ ${minutes % 60} phút`;
}

function statusTag(status: ProductImportHistorySummary['status']) {
  const labels: Record<ProductImportHistorySummary['status'], { color: string; text: string }> = {
    uploading: { color: 'blue', text: 'Đang tải dữ liệu' },
    queued: { color: 'cyan', text: 'Đang chờ xử lý' },
    processing: { color: 'processing', text: 'Đang xử lý' },
    completed: { color: 'success', text: 'Hoàn tất' },
    failed: { color: 'error', text: 'Thất bại' },
  };
  return <Tag color={labels[status].color}>{labels[status].text}</Tag>;
}

const isActive = (status: ProductImportHistorySummary['status']) =>
  status === 'uploading' || status === 'queued' || status === 'processing';

const processedCount = (history: ProductImportHistorySummary) =>
  history.created_count + history.updated_count + history.error_count + history.skipped_duplicate_count;

/** The current user's active runs only: other users' stalled uploads must not drive this poll. */
async function listActiveImportHistories(): Promise<ProductImportHistorySummary[]> {
  const pageSize = 100;
  const first = await productService.listImportHistory({ page: 1, page_size: pageSize, active_only: true, mine: true });
  const remaining = await fetchRemainingPages(first.total, pageSize, (page) =>
    productService.listImportHistory({ page, page_size: pageSize, active_only: true, mine: true }));
  return [first, ...remaining].flatMap((page) => page.data);
}

function rowStatusTag(status: ProductImportHistoryRow['status']) {
  if (status === 'created') return <Tag color="success">Đã tạo</Tag>;
  if (status === 'updated') return <Tag color="blue">Đã cập nhật định mức</Tag>;
  if (status === 'error') return <Tag color="error">Lỗi</Tag>;
  return <Tag>Bỏ qua — đã tồn tại</Tag>;
}

export interface ProductImportHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function ProductImportHistoryDrawer({ open, onClose }: ProductImportHistoryDrawerProps) {
  const qc = useQueryClient();
  const screens = Grid.useBreakpoint();
  const [historyPage, setHistoryPage] = useState(1);
  const [selectedHistory, setSelectedHistory] = useState<ProductImportHistorySummary | null>(null);
  const [rowPage, setRowPage] = useState(1);
  const [rowStatus, setRowStatus] = useState<ProductImportHistoryRow['status'] | undefined>(undefined);
  const [now, setNow] = useState(Date.now());
  const [postCompletionRefreshUntil, setPostCompletionRefreshUntil] = useState(0);
  const previousSelection = useRef<{ id: string; status: ProductImportHistorySummary['status'] } | null>(null);
  const observedStatuses = useRef(new Map<string, ProductImportHistorySummary['status']>());

  // This observer stays mounted with the product toolbar even when the drawer/modal closes.
  // Keep watching active server-owned imports so their completion refreshes the product cache.
  const activeHistoryQuery = useQuery({
    queryKey: ['products', 'import-history', 'active-observer'],
    queryFn: async () => {
      const active = await listActiveImportHistories();
      const activeIds = new Set(active.map((history) => history.history_id));
      // Active-only discovery excludes terminal runs. Resolve only disappeared
      // tracked IDs so completion still invalidates products after the UI closes.
      const finished = await Promise.all([...observedStatuses.current]
        .filter(([id, status]) => isActive(status) && !activeIds.has(id))
        .map(([id]) => productService.getImportHistory(id)));
      return [...active, ...finished];
    },
    refetchInterval: (query) => query.state.data?.some((history) => isActive(history.status)) ? 3000 : false,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const historyQuery = useQuery({
    queryKey: ['products', 'import-history', historyPage, PAGE_SIZE],
    queryFn: () => productService.listImportHistory({ page: historyPage, page_size: PAGE_SIZE }),
    enabled: open,
    refetchInterval: (query) =>
      open && query.state.data?.data.some((history) => isActive(history.status)) ? 3000 : false,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const rowsQuery = useQuery({
    queryKey: ['products', 'import-history', selectedHistory?.history_id, 'rows', rowStatus, rowPage, PAGE_SIZE],
    queryFn: () => productService.listImportHistoryRows(selectedHistory!.history_id, {
      page: rowPage,
      page_size: PAGE_SIZE,
      ...(rowStatus ? { status: rowStatus } : {}),
    }),
    enabled: open && Boolean(selectedHistory),
    refetchInterval: open && selectedHistory &&
      (isActive(selectedHistory.status) || Date.now() < postCompletionRefreshUntil) ? 1500 : false,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const histories = historyQuery.data?.data ?? [];
  const hasProcessing = histories.some((history) => isActive(history.status));
  useEffect(() => {
    const latestStatuses = new Map<string, ProductImportHistorySummary['status']>();
    let reachedTerminal = false;
    for (const history of activeHistoryQuery.data ?? []) {
      const previousStatus = observedStatuses.current.get(history.history_id);
      if (previousStatus && isActive(previousStatus) && !isActive(history.status)) reachedTerminal = true;
      if (isActive(history.status)) latestStatuses.set(history.history_id, history.status);
    }
    observedStatuses.current = latestStatuses;
    if (reachedTerminal) void qc.invalidateQueries({ queryKey: productKeys.all });
  }, [activeHistoryQuery.data, qc]);
  useEffect(() => {
    if (!open) return;
    setSelectedHistory(null);
    setHistoryPage(1);
    setRowPage(1);
    setPostCompletionRefreshUntil(0);
    previousSelection.current = null;
    setNow(Date.now());
  }, [open]);

  useEffect(() => {
    if (!selectedHistory) return;
    const refreshed = histories.find((history) => history.history_id === selectedHistory.history_id);
    if (refreshed) setSelectedHistory(refreshed);
  }, [histories, selectedHistory?.history_id]);

  useEffect(() => {
    if (!selectedHistory) {
      previousSelection.current = null;
      return;
    }
    const previous = previousSelection.current;
    if (previous?.id === selectedHistory.history_id && isActive(previous.status) && !isActive(selectedHistory.status)) {
      setPostCompletionRefreshUntil(Date.now() + 15_000);
      void rowsQuery.refetch();
    }
    previousSelection.current = { id: selectedHistory.history_id, status: selectedHistory.status };
  }, [selectedHistory?.history_id, selectedHistory?.status, rowsQuery.refetch]);

  useEffect(() => {
    if (!open || (!hasProcessing && now >= postCompletionRefreshUntil)) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [open, hasProcessing, now, postCompletionRefreshUntil]);

  const historyColumns = useMemo<TableProps<ProductImportHistorySummary>['columns']>(() => [
    {
      title: 'Nguồn',
      dataIndex: 'source',
      render: (source: ProductImportHistorySummary['source']) => sourceLabels[source],
    },
    { title: 'Trạng thái', dataIndex: 'status', render: (_: unknown, record) => statusTag(record.status) },
    { title: 'Thời gian', dataIndex: 'started_at', render: (value: string) => formatDateTime(value) },
    {
      title: 'Thời lượng',
      dataIndex: 'duration_ms',
      render: (duration: number, record) => formatDuration(
        isActive(record.status) ? Math.max(duration, now - new Date(record.started_at).getTime()) : duration,
      ),
    },
    { title: 'Người import', dataIndex: 'started_by_name' },
    {
      title: 'Tiến độ',
      render: (_: unknown, record) => `${processedCount(record)}/${record.total_rows} đã xử lý · ${record.received_rows}/${record.total_rows} đã nhận`,
    },
    { title: 'Tạo thành công', dataIndex: 'created_count', render: (value: number) => `${value} tạo thành công` },
    { title: 'Cập nhật định mức', dataIndex: 'updated_count', render: (value?: number) => `${value ?? 0} cập nhật` },
    { title: 'Lỗi', dataIndex: 'error_count', render: (value: number) => `${value} lỗi` },
    {
      title: 'SKU trùng',
      dataIndex: 'skipped_duplicate_count',
      render: (value: number) => `${value} SKU trùng bị bỏ qua`,
    },
  ], [now]);
  const selectedHistoryId = selectedHistory?.history_id;

  const rowColumns = useMemo<TableProps<ProductImportHistoryRow>['columns']>(() => [
    { title: 'SKU', dataIndex: 'sku', render: (sku: string) => <Typography.Text code>{sku}</Typography.Text> },
    { title: 'ID sản phẩm', dataIndex: 'product_id', render: (productId?: string) => productId || '—' },
    { title: 'Trạng thái', dataIndex: 'status', render: (_: unknown, record) => rowStatusTag(record.status) },
    { title: 'Thông báo', dataIndex: 'message', render: (message?: string) => message || '—' },
  ], []);

  const close = () => {
    setSelectedHistory(null);
    setRowStatus(undefined);
    onClose();
  };
  const showList = () => {
    setSelectedHistory(null);
    setRowPage(1);
    setRowStatus(undefined);
  };

  return (
    <Drawer
      title={selectedHistory ? 'Chi tiết lượt import' : 'Lịch sử import sản phẩm'}
      open={open}
      onClose={close}
      width={screens.md ? 1000 : '100vw'}
      destroyOnClose
    >
      {selectedHistory ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={showList}>Quay lại danh sách</Button>
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="Nguồn">{sourceLabels[selectedHistory.source]}</Descriptions.Item>
            <Descriptions.Item label="Trạng thái">{statusTag(selectedHistory.status)}</Descriptions.Item>
            <Descriptions.Item label="Thời gian">{formatDateTime(selectedHistory.started_at)}</Descriptions.Item>
            <Descriptions.Item label="Người import">{selectedHistory.started_by_name}</Descriptions.Item>
            <Descriptions.Item label="Tổng dòng">{selectedHistory.total_rows}</Descriptions.Item>
            <Descriptions.Item label="Dữ liệu đã nhận">{selectedHistory.received_rows}/{selectedHistory.total_rows}</Descriptions.Item>
            <Descriptions.Item label="Dòng đã xử lý">{processedCount(selectedHistory)}/{selectedHistory.total_rows}</Descriptions.Item>
            <Descriptions.Item label="Thời lượng">{formatDuration(
              isActive(selectedHistory.status) ? Math.max(selectedHistory.duration_ms, now - new Date(selectedHistory.started_at).getTime()) : selectedHistory.duration_ms,
            )}</Descriptions.Item>
            <Descriptions.Item label="Tạo thành công">{selectedHistory.created_count}</Descriptions.Item>
            <Descriptions.Item label="Cập nhật định mức">{selectedHistory.updated_count ?? 0}</Descriptions.Item>
            <Descriptions.Item label="Lỗi">{selectedHistory.error_count}</Descriptions.Item>
            <Descriptions.Item label="SKU trùng bị bỏ qua">{selectedHistory.skipped_duplicate_count}</Descriptions.Item>
          </Descriptions>
          {isActive(selectedHistory.status) && <Progress
            percent={selectedHistory.total_rows ? Math.round((processedCount(selectedHistory) / selectedHistory.total_rows) * 100) : 0}
            format={() => `${processedCount(selectedHistory)}/${selectedHistory.total_rows} dòng đã xử lý`}
          />}
          {selectedHistory.failure_reason && <Tag color="error">Lý do thất bại: {selectedHistory.failure_reason}</Tag>}
          <Space direction="vertical" size={4}>
            <Typography.Text strong id="import-history-row-status-label">Lọc theo trạng thái</Typography.Text>
            <Select<'all' | ProductImportHistoryRow['status']>
              aria-labelledby="import-history-row-status-label"
              value={rowStatus ?? 'all'}
              style={{ width: 240, maxWidth: '100%' }}
              options={[
                { value: 'all', label: 'Tất cả' },
                { value: 'created', label: 'Tạo thành công' },
                { value: 'updated', label: 'Cập nhật định mức' },
                { value: 'error', label: 'Lỗi' },
                { value: 'skipped_duplicate', label: 'Bỏ qua — đã tồn tại' },
              ]}
              onChange={(status) => {
                setRowStatus(status === 'all' ? undefined : status);
                setRowPage(1);
              }}
            />
          </Space>
          <Spin spinning={rowsQuery.isLoading}>
            <Table<ProductImportHistoryRow>
              rowKey={(row) => `${row.row_index}-${row.sku}`}
              columns={rowColumns}
              dataSource={rowsQuery.data?.data ?? []}
              locale={{ emptyText: <Empty description={rowStatus ? 'Không có dòng import theo trạng thái này' : 'Chưa có dòng import'} /> }}
              scroll={{ x: 900 }}
              pagination={{
                current: rowPage,
                pageSize: PAGE_SIZE,
                total: rowsQuery.data?.total ?? 0,
                showSizeChanger: false,
                onChange: (page) => setRowPage(page),
              }}
            />
          </Spin>
        </Space>
      ) : (
        <Spin spinning={historyQuery.isLoading}>
          <Table<ProductImportHistorySummary>
            rowKey="history_id"
            columns={historyColumns}
            dataSource={histories}
            scroll={{ x: 1500 }}
            onRow={(record) => ({
              onClick: () => {
                if (selectedHistoryId !== record.history_id) setRowStatus(undefined);
                setSelectedHistory(record);
                setRowPage(1);
              },
              style: { cursor: 'pointer' },
            })}
            locale={{ emptyText: <Empty description="Chưa có lịch sử import" /> }}
            pagination={{
              current: historyPage,
              pageSize: PAGE_SIZE,
              total: historyQuery.data?.total ?? 0,
              showSizeChanger: false,
              onChange: (page) => setHistoryPage(page),
            }}
          />
        </Spin>
      )}
    </Drawer>
  );
}
